import { useEffect, useRef, useState } from 'react';
import { telegramMiniAppAuth } from '@/api/auth';
import { useTwa } from '@/app/TwaProvider';
import { Spinner } from '@/components/Spinner';
import { useAuthStore, useToastStore } from '@/store/auth';
import { loadLoginNotifications } from '@/store/loginNotifications';
import { getReferralCode } from '@/lib/referral';
import { mapAuthError } from '@/lib/authErrors';
import { useT } from '@/i18n/useT';

/**
 * Signs a Mini App visitor in automatically on first open.
 *
 * Same call the "TG Quick Sign In" button makes — POST the signed initData to
 * the miniapp-auth endpoint, which signs existing users in and auto-creates
 * first-timers — just triggered by opening the app instead of by a tap.
 *
 * Runs at most once per page load. A failure is non-fatal: the attempt is
 * marked done, the user lands on the normal logged-out UI, and the manual
 * button is still there.
 */
function isInstalledPwa(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches === true
    || (navigator as { standalone?: boolean }).standalone === true;
}

export function useTelegramAutoSignIn(): { pending: boolean } {
  const { ready, detecting, initData, isTelegram, user, getLiveInitData } = useTwa();
  const isAuthed = useAuthStore((s) => s.isAuthed());
  const setSession = useAuthStore((s) => s.setSession);
  const showError = useToastStore((s) => s.showError);
  const t = useT('login');
  const [pending, setPending] = useState(false);
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current || !ready) return;

    // An existing session wins — this must never re-run after a sign-out
    // either, which the ref guarantees for the rest of the page's life.
    if (isAuthed) { attempted.current = true; return; }

    // Two sources, deliberately trusted differently.
    //
    // The live SDK read is self-proving: only a real Telegram client ever
    // populates `WebApp.initData`. A regular browser and an installed PWA both
    // load the SDK script from index.html, but it leaves the field empty
    // there. This path is left unguarded so a native open can't be blocked by
    // a mis-detection.
    //
    // The context value may instead come from the `tgWebAppData` hash that
    // main.tsx caches into sessionStorage for the Telegram Web client. That is
    // just a URL parameter, so it is only honoured once TwaProvider has
    // confirmed a Mini App session and we are not running as an installed PWA.
    const liveInitData = getLiveInitData();
    const cachedInitData = isTelegram && !isInstalledPwa() ? initData : null;
    const signedInitData = liveInitData ?? cachedInitData;

    // No payload yet. While detection is still running that means "too early",
    // not "not Telegram", so leave the attempt open and wait for a later tick.
    if (!signedInitData) {
      if (!detecting) attempted.current = true;
      return;
    }

    attempted.current = true;
    setPending(true);

    void (async () => {
      try {
        const r = await telegramMiniAppAuth({
          initData: signedInitData,
          invitation: getReferralCode() ?? undefined,
        });
        setSession(r.user, r.token, {
          source: 'telegram-miniapp',
          telegramId: user ? String(user.id) : null,
          blockedProviders: r.blockedProviders,
        });
        // Sign-in announcements: fetched here and nowhere else (see
        // loadLoginNotifications).
        void loadLoginNotifications();
      } catch (e) {
        // Don't strand the user on a spinner — drop them on the logged-out UI
        // with the reason, where the manual sign-in paths still work.
        const message = mapAuthError(e, t);
        showError(message ?? (e instanceof Error ? e.message : t('login_failed')));
      } finally {
        setPending(false);
      }
    })();
  }, [ready, detecting, isAuthed, initData, isTelegram, user, getLiveInitData, setSession, showError, t]);

  return { pending };
}

/** Full-height placeholder shown while the automatic sign-in is in flight. */
export function AutoSignInLoader() {
  const t = useT('login');
  return (
    <div className="app-shell-h flex flex-col items-center justify-center gap-3 bg-surface">
      <Spinner containerClassName="" />
      <p className="text-[0.875rem] text-app/60">{t('signing_in')}</p>
    </div>
  );
}
